<script setup>
import { useAccountStatusIssues } from '@/account/composables/useAccountStatusIssues'
import { useSharableMembershipsCount } from '@/account/composables/useSharableMembershipsCount'

const { t } = useI18n()
const { currentPath } = useRoutes()
const { unagreedPolicies } = useSessionData()
const { features } = useFeatures()
const { sharableMembershipsCount } = useSharableMembershipsCount()

const { issues } = useAccountStatusIssues()
const accountStatusIssuesCount = toRef(() => issues.value?.length)

const { getFeatureRouteVisibilityByKey } = useFeatureRoutes()

function isItemActive(item) {
  if (item.urlMatch !== undefined) {
    if (!item.urlMatch || !(item.urlMatch instanceof RegExp))
      return
    return item.urlMatch.test(currentPath.value)
  }
  const pathRegex = new RegExp(`^${item.url}`)
  return pathRegex.test(currentPath.value)
}

const useNewCalendar = computed(() => features.customer_v2_schedule && !(features.customer_v2_calendar_allow_user_toggle && !features.customer_v2_calendar_user_toggle))

const myAccountBadgeCount = computed(() =>
  accountStatusIssuesCount.value + sharableMembershipsCount.value,
)

const navMenuItems = computed(() => {
  const items = [
    {
      icon: 'i-far-user',
      iconAlt: 'i-fas-user',
      label: t('My Account'),
      // urlMatch: /^\/$/g,
      url: '/account',
      visible: getFeatureRouteVisibilityByKey('my_account'),
      routerLink: true,
      badge: myAccountBadgeCount.value,
      v2Link: true,
    },
    {
      icon: 'i-far-calendar',
      iconAlt: 'i-fas-calendar',
      label: t('Schedule'),
      url: useNewCalendar.value ? '/schedule' : '/',
      // url: '/schedule',
      urlMatch: useNewCalendar.value ? undefined : /^$/,
      visible: getFeatureRouteVisibilityByKey('schedule'),
      v2Link: useNewCalendar.value,
      routerLink: useNewCalendar.value,
    },
    {
      icon: 'i-far-video',
      iconAlt: 'i-fas-video',
      label: t('On Demand'),
      url: '/on_demand',
      visible: getFeatureRouteVisibilityByKey('on_demand'),
      routerLink: true,
      v2Link: true,
    },
    {
      icon: 'i-far-id-card',
      iconAlt: 'i-fas-id-card',
      label: t('Memberships'),
      urlMatch: /^\/memberships?/g,
      url: '/memberships',
      visible: getFeatureRouteVisibilityByKey('memberships'),
      v2Link: false,
    },
    {
      icon: 'i-far-folders',
      iconAlt: 'i-fas-folders',
      label: t('Courses'),
      url: '/courses',
      visible: getFeatureRouteVisibilityByKey('courses'),
      v2Link: false,
    },
    {
      icon: 'i-far-calendar-check',
      iconAlt: 'i-fas-calendar-check',
      label: t('Appointments'),
      url: '/appointment_types',
      v2Link: features.customer_appointments_v2,
      visible: getFeatureRouteVisibilityByKey('appointments'),
    },
    {
      icon: 'i-far-cart-shopping',
      iconAlt: 'i-fas-cart-shopping',
      label: t('Store'),
      url: '/store',
      visible: getFeatureRouteVisibilityByKey('store'),
      v2Link: features?.customer_store_v2,
    },
    {
      icon: 'i-far-folder-open',
      iconAlt: 'i-fas-folder-open',
      label: t('Forms and Waivers'),
      url: '/prerequisites/onboarding',
      visible: getFeatureRouteVisibilityByKey('forms_and_waivers'),
      v2Link: features?.waiver_signatures,
    },
  ]
  return items.map(item => ({
    ...item,
    visible: item.visible !== false,
    active: isItemActive(item),
  }))
})

const activeItem = computed(() => {
  return navMenuItems.value.find(item => item.active)
})

const visibleItemsCount = computed(() => navMenuItems.value?.filter(item => item.visible !== false).length || 0)
</script>

<template>
  <slot v-bind="{ navMenuItems, activeItem, visibleItemsCount }" />
</template>

<i18n lang="json" locale="en-us">
{
  "My Account": "My Account",
  "Schedule": "Schedule",
  "On Demand": "On Demand",
  "Memberships": "Memberships",
  "Courses": "Courses",
  "Appointments": "Appointments",
  "Store": "Store",
  "Forms and Waivers": "Forms and Waivers"
}
</i18n>

<i18n locale="de" lang="json">
{
  "My Account": "Mein Konto",
  "Schedule": "Planen",
  "On Demand": "Auf Abruf",
  "Memberships": "Mitgliedschaften",
  "Courses": "Kurse",
  "Appointments": "Termine",
  "Store": "Shop",
  "Forms and Waivers": "Formulare und Verzichterklärungen"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "My Account": "Mon compte",
  "Schedule": "Planning",
  "On Demand": "À la demande",
  "Memberships": "Abonnements",
  "Courses": "Stages",
  "Appointments": "Rendez-vous",
  "Store": "Boutique",
  "Forms and Waivers": "Formulaires et attestations"
}
</i18n>

<i18n locale="es" lang="json">
{
  "My Account": "Mi cuenta",
  "Schedule": "Horario",
  "On Demand": "A la carta",
  "Memberships": "Abonos",
  "Courses": "Cursos",
  "Appointments": "Citas",
  "Store": "Tienda",
  "Forms and Waivers": "Cláusulas y Preguntas"
}
</i18n>
